/**
 * axios setup to use mock service
 */

import axios, { AxiosHeaders, AxiosRequestConfig } from 'axios';
import { applyPermissionContextHeaders } from './permissionContext';

const TENANT_ID_STORAGE_KEY = 'tenantId';
const TENANT_HEADER_NAME = 'X-Tenant-Id';
let permissionCompanyId: number | null = null;
let permissionSiteId: number | null = null;

const getStorageItem = (key: string): string | null => {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
};

let selectedTenantId: string | null = getStorageItem(TENANT_ID_STORAGE_KEY);

function applyTenantHeader(tenantId: string | null) {
  if (selectedTenantId !== tenantId) {
    permissionCompanyId = null;
    permissionSiteId = null;
  }
  selectedTenantId = tenantId;
}

const axiosServices = axios.create({ baseURL: window.appConfig?.apiUrl || 'http://localhost:3010/' });

applyTenantHeader(getStorageItem(TENANT_ID_STORAGE_KEY));

// ==============================|| AXIOS - FOR MOCK SERVICES ||============================== //

axiosServices.interceptors.request.use(
  async (config) => {
    const headers = AxiosHeaders.from(config.headers);

    const accessToken = getStorageItem('serviceToken');
    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    } else {
      headers.delete('Authorization');
    }

    const tenantId = getStorageItem(TENANT_ID_STORAGE_KEY) ?? selectedTenantId;
    if (headers.has(TENANT_HEADER_NAME) || tenantId) {
      if (!headers.has(TENANT_HEADER_NAME)) {
        headers.set(TENANT_HEADER_NAME, tenantId);
      }
    } else {
      headers.delete(TENANT_HEADER_NAME);
    }

    if (selectedTenantId && headers.get(TENANT_HEADER_NAME) === selectedTenantId) {
      applyPermissionContextHeaders(headers, permissionCompanyId, permissionSiteId);
    }
    config.headers = headers;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosServices.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error;

    // Optional: a request that never reached the server has no response at all, and reading
    // .status off it threw a TypeError that replaced the real failure with a confusing one.
    if (response?.status === 401 && !window.location.href.includes('/login')) {
      const returnTo = window.location.pathname + window.location.search + window.location.hash;
      window.location.assign('/login?returnTo=' + encodeURIComponent(returnTo));
    }

    // The API answers most failures with ProblemDetails, and that body is the useful part.
    if (response?.data) {
      return Promise.reject(response.data);
    }

    // A failure with no body is a route that is not there (commonly an API build older than the
    // client calling it), a method that is not allowed, or an authorization refusal. Reporting
    // those as a bare "Wrong Services" hid the status, which is the only thing that tells them
    // apart — so say what actually came back, and from where.
    if (response) {
      const method = error.config?.method?.toUpperCase() ?? 'Request';
      const url = error.config?.url ?? '';
      return Promise.reject({ message: `${method} ${url} failed: ${response.status} ${response.statusText ?? ''}`.trim() });
    }

    return Promise.reject({ message: error.message || 'The server did not respond.' });
  }
);

export default axiosServices;

export function syncTenantHeader(tenantId: number | string | null) {
  applyTenantHeader(tenantId == null ? null : String(tenantId));
}

export function syncPermissionContext(companyId: number | null, siteId: number | null) {
  permissionCompanyId = companyId;
  permissionSiteId = siteId;
}

export async function fetcher(args: string | [string, AxiosRequestConfig]) {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosServices.get(url, { ...config });

  return res.data;
}
